// Implements: F-MCK-002, F-MCK-011, F-UIX-009, F-UIX-010
/**
 * The gallery pages, as ONE infinite query per set of filters under `["mockups","list", filters]`.
 *
 * The cursor is opaque: the hook hands back whatever `next_cursor` the last
 * page carried and nothing else, so a backend that changes how it paginates
 * changes nothing here. `hasNextPage` is the only thing the container needs
 * to decide whether to offer "load more". The filters are part of the key, so
 * a change of filter starts a list of its own and the old one stays cached;
 * every mutation invalidates the shared prefix and refreshes them all.
 */

import { useInfiniteQuery } from "@tanstack/react-query";
import type { InfiniteData, UseInfiniteQueryResult } from "@tanstack/react-query";

import type { ApiError } from "../../../shared/api/ApiError";
import { MOCKUPS_KEY } from "../../../shared/api/queryKeys";
import type { MockupPageView } from "../../../types/api";
import { NO_FILTERS } from "../api/galleryFilters";
import type { GalleryFilters } from "../api/galleryFilters";
import { fetchMockupPage } from "../api/mockupsApi";

export type UseMockupsResult = UseInfiniteQueryResult<InfiniteData<MockupPageView>, ApiError>;

export function useMockups(filters: GalleryFilters = NO_FILTERS, enabled = true): UseMockupsResult {
  return useInfiniteQuery<
    MockupPageView,
    ApiError,
    InfiniteData<MockupPageView>,
    readonly [...typeof MOCKUPS_KEY, GalleryFilters],
    string | undefined
  >({
    queryKey: [...MOCKUPS_KEY, filters],
    queryFn: ({ pageParam }) => fetchMockupPage(pageParam, filters),
    enabled,
    initialPageParam: undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });
}
